<template>
    <NuxtLink :to="educationLink" class="education-card shrink-0 w-[300px] block cursor-pointer">
        <!-- Image -->
        <div class="relative h-[220px] rounded-t-3xl overflow-hidden bg-gray-200 dark:bg-surface-subtle">
            <CThumbnailImage
                v-if="education.imageUrl"
                :src="education.imageUrl"
                :alt="education.title"
                type="skill"
                class="size-full"
            />
            <div v-else class="size-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/30 dark:to-purple-900/30">
                <span class="text-4xl">📚</span>
            </div>
            <!-- Match Score Badge -->
            <div
                v-if="education.matchScore"
                class="absolute top-3 right-3 bg-white/90 dark:bg-surface-card/90 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-sm"
            >
                <span class="text-sm font-semibold text-orange-600 dark:text-orange-400">{{ education.matchScore }}%</span>
            </div>
        </div>

        <!-- Content -->
        <div class="bg-white dark:bg-surface-card p-4 rounded-b-3xl flex flex-col gap-3">
            <div class="flex flex-col gap-3">
                <!-- Title & Type -->
                <div class="flex flex-col gap-1">
                    <h3 class="font-semibold text-lg text-[#1d191f] dark:text-white tracking-tight leading-normal line-clamp-2">
                        {{ education.title }}
                    </h3>
                    <p class="text-base text-[#6c5d73] dark:text-gray-300 leading-normal">
                        {{ education.type }}
                    </p>
                </div>

                <!-- Organization -->
                <p v-if="education.organization" class="font-semibold text-sm text-[#1d191f] dark:text-white leading-5">
                    <span class="text-[#047274] dark:text-primary-400">#</span>{{ education.organization }}
                </p>

                <!-- Tags -->
                <div class="flex flex-wrap gap-2">
                    <span
                        v-for="tag in education.tags"
                        :key="tag"
                        class="font-semibold text-sm text-[#1d191f] dark:text-white leading-5"
                    >
                        <span class="text-[#047274] dark:text-primary-400">#</span>{{ tag }}
                    </span>
                </div>
            </div>
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import CThumbnailImage from '@/components/ui/CThumbnailImage.vue'

interface Education {
    title: string
    type?: string
    organization?: string
    imageUrl?: string
    tags: string[]
    slug?: string
    matchScore?: number
}

const props = defineProps<{
    education: Education
}>()

const educationLink = computed(() => {
    if (props.education.slug) {
        return `/vzdelavanie/${props.education.slug}`
    }
    // Fallback to title-based slug
    const slug = props.education.title.toLowerCase().replace(/\s+/g, '-')
    return `/vzdelavanie/${slug}`
})
</script>

<style scoped>
.education-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.education-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

.education-card :deep(.CThumbnailImage) {
    aspect-ratio: auto;
    border-radius: 0;
}
</style>
